import { useEffect, useState } from 'react';
import axios from 'axios';
import Reveal from '../components/site/Reveal';
import SectionHeader from '../components/site/SectionHeader';
import { COMMITMENTS } from '../data/site';
import { useT } from '../i18n/LanguageContext';
import { useHeroImages } from '../lib/useHeroImages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_HERO = 'https://images.unsplash.com/photo-1748525591986-58d2f01d4170?crop=entropy&cs=srgb&fm=jpg&q=85';

export default function Strategy() {
  const { t } = useT();
  const heroes = useHeroImages();
  const [pillars, setPillars] = useState([]);
  const [clusters, setClusters] = useState([]);

  useEffect(() => {
    axios.get(`${API}/pillars`).then((r) => setPillars(r.data || [])).catch(() => {});
    axios.get(`${API}/clusters`).then((r) => setClusters(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="page-strategy">
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={heroes.strategy || DEFAULT_HERO} alt="" className="w-full h-full object-cover hero-img hero-img-strong" />
          <div className="absolute inset-0" style={{ background: 'var(--hero-overlay)' }} />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <p className="eyebrow mb-6">{t('strategy.eyebrow')}</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[84px] leading-[0.95] tracking-tighter text-prim max-w-5xl">
              {t('strategy.title')}
            </h1>
            <p className="mt-8 text-sec text-lg md:text-xl max-w-3xl leading-relaxed">
              {t('strategy.intro')}
            </p>
            <p className="mt-10 font-arabic text-2xl text-brand-teal-light/70 max-w-3xl leading-relaxed">
              "نَعْتَمِدُ نَمُوذَجَ نُمُوٍّ مُسْتَدَامٍ — اقْتِصَادٌ تَنَافُسِيٌّ، مُنْتِجٌ، مُتَنَوِّعٌ، مُبْدِعٌ"
            </p>
            <p className="mt-3 text-muted italic max-w-2xl">"Adopt a sustainable growth model — a competitive, productive, diversified and innovative economy concentrating on specialised clusters where Qatar can establish competitive advantage." — NDS3 2024–2030</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <SectionHeader
              eyebrow="— Seven National Outcomes"
              title={t('strategy.pillarsTitle')}
              lede="NDS3 organises Qatar's transformation around these outcomes — the standards by which every MBK venture is underwritten."
            />
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pillars.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 100}>
                <div data-testid={`pillar-${p.n}`} className="surface-2 border border-subtle p-8 rounded-md card-glow h-full relative overflow-hidden group">
                  <div className="absolute -top-6 -right-4 font-display text-[120px] leading-none text-prim/[0.04] group-hover:text-brand-teal-light/[0.08] transition-colors">{p.n}</div>
                  <div className="relative">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-brand-teal-light">{p.n} · NDS3</span>
                      <span className="font-arabic text-2xl text-muted">{p.arabic}</span>
                    </div>
                    <h3 className="font-display text-2xl text-prim mt-5 tracking-tight">{p.title}</h3>
                    <p className="mt-3 text-sec leading-relaxed text-sm">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-subtle bg-base">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <SectionHeader
              eyebrow="— How our portfolio maps to NDS3 clusters"
              title={t('strategy.clusterTitle')}
              lede="A direct mapping between MBK ventures and the high-priority economic clusters identified by Qatar's Third National Development Strategy."
            />
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {clusters.map((c, i) => (
              <Reveal key={c.id} delay={(i % 2) * 120}>
                <div data-testid={`cluster-${i}`} className="surface-2 border border-subtle p-8 md:p-10 rounded-md card-glow h-full">
                  <div className="flex items-baseline justify-between">
                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-brand-teal-light">0{i + 1} · NDS3 Cluster</p>
                    <span className="font-arabic text-3xl text-muted">{c.arabic}</span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-prim mt-4 tracking-tight">{c.cluster}</h3>
                  <p className="mt-3 text-sec leading-relaxed">{c.desc}</p>
                  {c.ventures?.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-subtle">
                      <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted mb-3">MBK companies advancing this cluster</p>
                      <ul className="flex flex-wrap gap-2">
                        {c.ventures.map((v) => (
                          <li key={v} className="px-3 py-1.5 rounded-full border border-brand-teal/30 bg-brand-teal/5 text-brand-teal-light text-xs font-mono">{v}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <SectionHeader eyebrow="— Three commitments" title="How we deliver against the vision." />
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.title} delay={i * 120}>
                <div className="surface-2 border border-subtle p-8 md:p-10 rounded-md card-glow h-full">
                  <p className="font-arabic text-5xl text-brand-teal-light/80">{c.arabic}</p>
                  <h4 className="font-display text-xl text-prim mt-5 tracking-tight">{c.title}</h4>
                  <p className="mt-3 text-sec leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-20 text-center">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted">Anchored in Qatar · Engineered for the world</p>
              <p className="mt-3 font-display text-2xl text-brand-teal-light">QNV 2030 · NDS3 2024–2030</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
